import React, { createContext, useContext, useState, useEffect } from 'react';
import { useErrorTracking } from '../hooks/useAnalytics';
import { error, info, debug } from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { trackError } = useErrorTracking();

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    // CRITICAL: Validate token is a real JWT, not a test token
    // Real JWTs start with "eyJ" and are at least 50 characters
    const isValidToken = token && 
                         token.length >= 50 && 
                         token.startsWith('eyJ') && 
                         !token.includes('test') &&
                         token !== 'test-token';
    
    if (!isValidToken && token) {
      // Clear invalid/test tokens IMMEDIATELY
      error('❌ CRITICAL: Invalid token detected in localStorage, clearing immediately', {
        tokenValue: token,
        tokenLength: token.length,
        isTestToken: token === 'test-token',
        startsWithEyJ: token.startsWith('eyJ')
      });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setLoading(false);
      return;
    }
    
    // Also check if token is "test-token" explicitly
    if (token === 'test-token') {
      error('❌ CRITICAL: test-token found in localStorage - clearing immediately');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setLoading(false);
      return;
    }
    
    if (isValidToken && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (err) {
        error('Error parsing user data', { error: err.message, stack: err.stack });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/enhanced-auth?action=login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Save token to localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        debug('🔐 Token saved', {
          tokenLength: data.token ? data.token.length : 0
        });

        setUser(data.user);
        return { success: true };
      } else {
        // Extract error message from structured error response
        const errorMessage = data.error?.message || data.error || 'Login failed';

        // Track API error
        trackError(new Error(errorMessage), {
          action: 'login',
          endpoint: '/api/enhanced-auth?action=login',
          statusCode: response.status
        });

        // Handle email verification needed
        if (data.needsEmailVerification) {
          return {
            success: false,
            error: errorMessage,
            needsEmailVerification: true,
            userId: data.userId
          };
        }
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      error('Login error', { error: err.message, stack: err.stack });
      trackError(err, {
        action: 'login',
        endpoint: '/api/enhanced-auth?action=login',
        errorType: 'network'
      });
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await fetch('/api/enhanced-auth?action=signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, name })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Don't auto-login for new registrations - they need to verify email first
        return {
          success: true,
          message: data.message,
          needsEmailVerification: true
        };
      } else {
        // Extract error message from structured error response
        const errorMessage = data.error?.message || data.error || 'Registration failed';

        // Track API error
        trackError(new Error(errorMessage), {
          action: 'register',
          endpoint: '/api/enhanced-auth?action=signup',
          statusCode: response.status
        });
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      error('Register error', { error: err.message, stack: err.stack });
      trackError(err, {
        action: 'register',
        endpoint: '/api/enhanced-auth?action=signup',
        errorType: 'network'
      });
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await fetch('/api/enhanced-auth?action=resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Extract error message from structured error response
        const errorMessage = data.error?.message || data.error || 'Failed to resend verification';

        // Track API error
        trackError(new Error(errorMessage), {
          action: 'resend-verification',
          endpoint: '/api/enhanced-auth?action=resend-verification',
          statusCode: response.status
        });
      }

      return data;
    } catch (err) {
      error('Resend verification error', { error: err.message, stack: err.stack });
      trackError(err, {
        action: 'resend-verification',
        endpoint: '/api/enhanced-auth?action=resend-verification',
        errorType: 'network'
      });
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await fetch('/api/enhanced-auth?action=forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Extract error message from structured error response
        const errorMessage = data.error?.message || data.error || 'Failed to send reset email';

        // Track API error
        trackError(new Error(errorMessage), {
          action: 'forgot-password',
          endpoint: '/api/enhanced-auth?action=forgot-password',
          statusCode: response.status
        });
      }

      return data;
    } catch (err) {
      error('Forgot password error', { error: err.message, stack: err.stack });
      trackError(err, {
        action: 'forgot-password',
        endpoint: '/api/enhanced-auth?action=forgot-password',
        errorType: 'network'
      });
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const response = await fetch('/api/enhanced-auth?action=reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Extract error message from structured error response
        const errorMessage = data.error?.message || data.error || 'Failed to reset password';

        // Track API error
        trackError(new Error(errorMessage), {
          action: 'reset-password',
          endpoint: '/api/enhanced-auth?action=reset-password',
          statusCode: response.status
        });
      }

      return data;
    } catch (err) {
      error('Reset password error', { error: err.message, stack: err.stack });
      trackError(err, {
        action: 'reset-password',
        endpoint: '/api/enhanced-auth?action=reset-password',
        errorType: 'network'
      });
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/enhanced-auth?action=change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Extract error message from structured error response
        const errorMessage = data.error?.message || data.error || 'Failed to change password';

        // Track API error
        trackError(new Error(errorMessage), {
          action: 'change-password',
          endpoint: '/api/enhanced-auth?action=change-password',
          statusCode: response.status
        });
      }

      return data;
    } catch (err) {
      error('Change password error', { error: err.message, stack: err.stack });
      trackError(err, {
        action: 'change-password',
        endpoint: '/api/enhanced-auth?action=change-password',
        errorType: 'network'
      });
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (err) {
        error('❌ AuthContext: Error parsing user data', { error: err.message, stack: err.stack });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } else {
      setUser(null);
    }
  };

  const fetchUserFromAPI = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        debug('🔍 No token found for API refresh');
        return null;
      }

      debug('🔄 Fetching fresh user data from API...');
      const response = await fetch('/api/user?action=profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          info('✅ Fresh user data received', { credits: data.user.credits });
          // Update both state and localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);
          return data.user;
        } else {
          error('❌ API returned unsuccessful response', { data });
        }
      } else {
        error('❌ API request failed', { status: response.status });
        // If API fails, don't clear user data - keep existing
      }
    } catch (err) {
      error('❌ Failed to fetch user from API', { error: err.message, stack: err.stack });
      // If network fails, don't clear user data - keep existing
    }
    return null;
  };

  const updateUserCredits = (newCredits) => {
    if (user) {
      info('💰 Updating user credits', { from: user.credits, to: newCredits });
      const updatedUser = { ...user, credits: newCredits };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    fetchUserFromAPI,
    updateUserCredits,
    resendVerification,
    forgotPassword,
    resetPassword,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
